/** @jsxRuntime classic */
/** @jsx jsx */
import React, { FC, ReactNode, useState } from 'react';
import { useLayer, useHover, Arrow } from 'react-laag';
import { StyledTooltip, bodyCss, closeButton, tooltipContentCss, tooltipSpacerCss } from './style';
import { jsx, useTheme } from '@emotion/react';
import { Icons, TPlacements } from '../../types/theme';
import { Button } from '../Button';
import { BnIcon } from '../icons_v2';

export interface Props {
	content: string | ReactNode;
	delayEnter?: number;
	delayLeave?: number;
	placement?: TPlacements;
	isTopLayer?: boolean;
	isMobile?: boolean;
}

const BULLET_LINE_PATTERN = /^\s*[-*•]\s+(.+)$/;

const renderContent = (content: string | ReactNode): string | ReactNode => {
	if (typeof content !== 'string') return content;

	const lines = content.split('\n').map(line => line.trim());
	const hasBullets = lines.some(line => BULLET_LINE_PATTERN.test(line));

	if (!hasBullets) return content;

	const renderedBlocks: ReactNode[] = [];
	let bulletItems: string[] = [];

	const flushBulletItems = (keySuffix: number): void => {
		if (!bulletItems.length) return;

		renderedBlocks.push(
			<ul key={`bullet-group-${keySuffix}`}>
				{bulletItems.map((item, bulletIdx) => (
					<li key={`bullet-${keySuffix}-${bulletIdx}`}>{item}</li>
				))}
			</ul>
		);
		bulletItems = [];
	};

	lines.forEach((line, lineIdx) => {
		const match = line.match(BULLET_LINE_PATTERN);
		if (match) {
			bulletItems.push(match[1]);
			return;
		}

		flushBulletItems(lineIdx);

		if (!line) {
			renderedBlocks.push(<div key={`spacer-${lineIdx}`} css={tooltipSpacerCss} />);
			return;
		}

		renderedBlocks.push(<p key={`paragraph-${lineIdx}`}>{line}</p>);
	});

	flushBulletItems(lines.length);

	return (
		<div css={tooltipContentCss}>
			{renderedBlocks}
		</div>
	);
};

export const Tooltip: FC<Props> = ({
	children,
	content,
	delayEnter = 10,
	delayLeave = 300,
	placement = 'top-center',
	isTopLayer = false,
	isMobile = false,
}) => {
	const colors = useTheme();
	const [isOver, hoverProps] = useHover({ delayEnter, delayLeave });
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const renderedContent = renderContent(content);

	const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
		isOpen: isMobile ? isOpen : isOver,
		auto: true,
		triggerOffset: 8,
		placement,
	});

	return (
		<div data-aid="index-Tooltip">
			{!isMobile ? (
				<div>
					<div {...triggerProps} {...hoverProps} className="utils_tooltip">
						{children}
					</div>
					{isOver &&
						renderLayer(
							<StyledTooltip
								theme={{ ...colors }}
								className="tooltip"
								{...layerProps}
								isTopLayer={isTopLayer}
								{...hoverProps}
							>
								{renderedContent}
								<Arrow {...arrowProps} />
							</StyledTooltip>
						)}
				</div>
			) : (
				<div>
					<div {...triggerProps} onClick={() => setIsOpen(true)} className="utils_tooltip">
						{children}
					</div>
					{isOpen &&
						renderLayer(
							<StyledTooltip theme={{ ...colors }} className="tooltip" {...layerProps} isTopLayer={isTopLayer}>
								<div css={closeButton}>
									<Button
										data-aid="button-tooltip-set"
										sizer="S"
										theme="basic"
										onClick={() => {
											setIsOpen(false);
										}}
									>
										<BnIcon icon={Icons.close} />
									</Button>
								</div>
								<div css={bodyCss}>{renderedContent}</div>
								<Arrow {...arrowProps} />
							</StyledTooltip>
						)}
				</div>
			)}
		</div>
	);
};
